import { Accordion, AccordionSummary } from '@material-ui/core';
import LayoutCalendar from 'core/layout/LayoutCalendar';
import TextLink from 'core/modules/atoms/TextLink/TextLink';
import AccordionTemplates from 'core/modules/components/lists/AccordionTemplates/AccordionTemplates';
import PropTypes from 'prop-types';
import React from 'react';
import BackButton from 'ui/atoms/BackButton/BackButton';
import Button from 'ui/atoms/Button/Button';
import Dialog from 'ui/organisms/dialogs/Dialog/Dialog';
import Icon from 'ui/atoms/Icon/Icon';
import InputRadio from 'ui/atoms/InputRadio/InputRadio';
import InputSearch from 'ui/atoms/InputSearch/InputSearch';
import Loading from 'ui/atoms/Loading/Loading';
import Pagination from 'ui/molecules/Pagination/Pagination';
import RadioGroup from 'ui/molecules/RadioGroup/RadioGroup';
import SelectCustomizable from 'ui/atoms/SelectCustomizable/SelectCustomizable';
import Switch from 'ui/atoms/Switch/Switch';
import TextHtml from 'ui/atoms/TextHtml/TextHtml';
import NoItems from 'core/modules/components/general/NoItems';
import HeaderCustom from 'core/modules/components/headers/HeaderCustom/HeaderCustom';
import ExerciseLibraryList from 'core/modules/components/lists/ExerciseLibraryList/ExerciseLibraryList';
import DialogSearchQuestionsCourseItems from './DialogSearchQuestionsCourseItems';
import useDialogSearchQuestionsNew, { ACTIVITY_SOURCE, QUESTION_AUTHOR } from './useDialogSearchQuestionsNew';

// Este diálogo de búsqueda se utiliza solamente en Tangerine por ahora, para EDV utilizar DialogSearchQuestionsContainer

const DialogSearchQuestionsNew = ({ t, open, onAdd, onCancel, alreadyAddedQuestions, questionType, courseGuid, selectJustOne }) => {
  const {
    search,
    isAutoevaluative,
    questions,
    selectedQuestions,
    total,
    totalPages,
    filtersEmpty,
    questionFilters,
    isLoading,
    showMobileMenu,
    selectedPage,
    selectedLearningObjectives,
    learningObjectives,
    currentCourseItems,
    recentSearches,
    onChangeSearch,
    onChangeIsAutoevaluative,
    onSelecteType,
    onChangeSelectedQuestions,
    onPageChange,
    onClose,
    onAddQuestions,
    onCleanFilters,
    onShowMobileMenu,
    onSelectLearningObjective,
    onRemoveLearningObjective,
    onSelectFilter,
    onSelectAuthor,
    onSelectSource,
    shouldShowActivitySourceFilter,
    onClickRemoveSearch,
  } = useDialogSearchQuestionsNew({ open, onAdd, onCancel, alreadyAddedQuestions, questionType, courseGuid });

  return (
    <Dialog
      size={'all-screen'}
      isOpen={open}
      className="dialog-search-questions-new"
      isHeaderHidden={true}
      onClose={onClose}
      ariaLabel={t('common:Activity library')}
    >
      <LayoutCalendar
        isMainLandmark={false}
        header={
          <HeaderCustom
            left={<BackButton color="dark" onClick={onClose} />}
            center={t('common:Activity library')}
            right={
              <Button
                isDisabled={selectedQuestions.length === 0}
                onClick={onAddQuestions}
                text={selectedQuestions.length === 0 ? t('actions:Add') : t('actions:Add X selected', { count: selectedQuestions.length })}
              />
            }
            onClose={null}
          />
        }
        responsive={true}
        showMobileMenu={showMobileMenu}
        onShowMobileMenu={onShowMobileMenu}
      >
        <div className="dialog-search-questions-new__left">
          <div className="dialog-search-questions-new__left-section">
            <div className="dialog-search-questions-new__left-section-header">
              <div className="dialog-search-questions-new__left-section-header-left">
                <TextHtml text={t('common:Filter by')} />
              </div>
              <div className="dialog-search-questions-new__left-section-header-right">
                <TextLink text={t('common:Clean filters')} onClick={onCleanFilters} disabled={filtersEmpty} />
              </div>
            </div>
          </div>
          <div className="dialog-search-questions-new__left-subsection">
            {/* -------- FILTER BY: AUTHOR ------- */}
            <Accordion className="dialog-search-questions-new__left-subsection-accordion" id={'filterAuthor'} defaultExpanded={true}>
              <AccordionSummary
                expandIcon={<Icon name="chevron-down" size="xl" />}
                aria-controls="filterAuthor-content"
                id="filterAuthor-header"
                className={'dialog-search-questions-new__left-subsection-header'}
              >
                <div className="dialog-search-questions-new__left-section-header-left">
                  <TextHtml text={t('common:Created by')} />
                </div>
              </AccordionSummary>
              <div className="dialog-search-questions-new__left-subsection-content">
                <RadioGroup isLegendHidden legend={t('common:Created by')} name="owners">
                  <InputRadio
                    label={t('common:All')}
                    value={QUESTION_AUTHOR.ALL}
                    checked={questionFilters.author === QUESTION_AUTHOR.ALL}
                    onChange={onSelectAuthor}
                  />
                  <InputRadio
                    label={t('common:Editorial')}
                    value={QUESTION_AUTHOR.EDITORIAL}
                    checked={questionFilters.author === QUESTION_AUTHOR.EDITORIAL}
                    onChange={onSelectAuthor}
                  />
                  <InputRadio
                    label={t('common:Teacher')}
                    value={QUESTION_AUTHOR.TEACHER}
                    checked={questionFilters.author === QUESTION_AUTHOR.TEACHER}
                    onChange={onSelectAuthor}
                  />
                </RadioGroup>
              </div>
            </Accordion>
          </div>
          {/* -------- FILTER BY: ACTIVITY SOURCE (oculto con autoría docente: no aplica libro/adicionales) ------- */}
          {shouldShowActivitySourceFilter && (
            <div className="dialog-search-questions-new__left-subsection">
              <Accordion className="dialog-search-questions-new__left-subsection-accordion" id={'filterActivitySource'} defaultExpanded={true}>
                <AccordionSummary
                  expandIcon={<Icon name="chevron-down" size="xl" />}
                  aria-controls="filterActivitySource-content"
                  id="filterActivitySource-header"
                  className={'dialog-search-questions-new__left-subsection-header'}
                >
                  <div className="dialog-search-questions-new__left-section-header-left">
                    <TextHtml text={t('common:Activity type')} />
                  </div>
                </AccordionSummary>
                <div className="dialog-search-questions-new__left-subsection-content">
                  <RadioGroup isLegendHidden legend={t('common:Activity type')} name="activitySource">
                    <InputRadio
                      label={t('common:All')}
                      value={ACTIVITY_SOURCE.ALL}
                      checked={questionFilters.source === ACTIVITY_SOURCE.ALL}
                      onChange={onSelectSource}
                    />
                    <InputRadio
                      label={t('common:Book activities')}
                      value={ACTIVITY_SOURCE.BOOK}
                      checked={questionFilters.source === ACTIVITY_SOURCE.BOOK}
                      onChange={onSelectSource}
                    />
                    <InputRadio
                      label={t('common:Additional activities')}
                      value={ACTIVITY_SOURCE.ADDITIONAL}
                      checked={questionFilters.source === ACTIVITY_SOURCE.ADDITIONAL}
                      onChange={onSelectSource}
                    />
                  </RadioGroup>
                </div>
              </Accordion>
            </div>
          )}
          <DialogSearchQuestionsCourseItems questionFilters={questionFilters} onSelectFilter={onSelectFilter} t={t} currentCourseItems={currentCourseItems} />

          {/* -------- FILTER BY: LEARNING  OBJECTIVES ------- */}
          {learningObjectives?.length > 0 && (
            <div className="dialog-search-questions-new__left-subsection">
              <Accordion className="dialog-search-questions-new__left-subsection-accordion" id={'filterLo'} defaultExpanded={true}>
                <AccordionSummary
                  expandIcon={<Icon name="chevron-down" size="xl" />}
                  aria-controls="filterLo-content"
                  id="filterLo-header"
                  className={'dialog-search-questions-new__left-subsection-header'}
                >
                  <div className="dialog-search-questions-new__left-section-header-left">
                    <TextHtml text={t('common:Learning objectives')} />
                  </div>
                </AccordionSummary>
                <div className="dialog-search-questions-new__left-subsection-content">
                  <div className="dialog-search-questions-new__left-subsection-option">
                    <SelectCustomizable
                      options={learningObjectives}
                      isClearable={true}
                      placeholder={t('placeholder:Select')}
                      onChange={onSelectLearningObjective}
                      value={null}
                    />
                  </div>
                  {selectedLearningObjectives.length > 0 && (
                    <div className="dialog-search-questions-new__left-subsection-option dialog-search-questions-new__select-container">
                      <SelectCustomizable
                        value={selectedLearningObjectives}
                        isMulti={true}
                        onChange={onRemoveLearningObjective}
                        isClearable={false}
                        placeholder={''}
                        noOptionsMessage={''}
                        isSearchable={false}
                        menuIsOpen={false}
                      />
                    </div>
                  )}
                </div>
              </Accordion>
            </div>
          )}
          {/* -------- FILTER BY: QUESTION TEMPLATE ------- */}

          <div className="dialog-search-questions-new__left-subsection">
            <Accordion className="dialog-search-questions-new__left-subsection-accordion" id={'filterTemplate'} defaultExpanded={true}>
              <AccordionSummary
                expandIcon={<Icon name="chevron-down" size="xl" />}
                aria-controls="filterTemplate-content"
                id="filterTemplate-header"
                className={'dialog-search-questions-new__left-subsection-header'}
              >
                <div className="dialog-search-questions-new__left-section-header-left">
                  <TextHtml text={t('common:Template')} />
                </div>
              </AccordionSummary>
              <AccordionTemplates t={t} onSelecteType={onSelecteType} resetSelection={filtersEmpty} filter={questionType} />
            </Accordion>
          </div>
        </div>
        <div className="dialog-search-questions-new__right">
          <div className="dialog-search-questions-new__header">
            <div className="dialog-search-questions-new__header-left">
              <output className="dialog-search-questions-new__header-left-founds">{t('common:X found', { count: total })}</output>
            </div>
            <div className="dialog-search-questions-new__header-right">
              <div className="dialog-search-questions-new__search">
                <InputSearch
                  placeholder={t('placeholder:Search')}
                  onChange={onChangeSearch}
                  value={search}
                  recentSearches={recentSearches}
                  onClickRemoveSearch={onClickRemoveSearch}
                  iconPosition="right"
                />
              </div>
            </div>
          </div>
          <div className="dialog-search-questions-new__content">
            {isLoading && <Loading />}
            {!isLoading && questions.length > 0 && (
              <ExerciseLibraryList
                t={t}
                items={questions}
                legend={t('common:Activity library')}
                isLegendHidden
                selectedItems={selectedQuestions}
                onChangeSelected={onChangeSelectedQuestions}
                selectJustOne={selectJustOne}
              />
            )}
            {!isLoading && questions.length === 0 && (
              <div className="dialog-search-questions__no-results">
                <NoItems imageId="noQuestionsNew" text={t('content:No questions found')} size="medium" />
              </div>
            )}
          </div>

          <div className="dialog-search-questions-new__footer">
            <div className="dialog-search-questions-new__footer-left">
              {totalPages > 1 && <Pagination currentPage={selectedPage} onPageChange={onPageChange} totalPages={totalPages} />}
            </div>
            <div className="dialog-search-questions-new__footer-right">
              <div className="dialog-search-questions__only-autoevaluations">
                <Switch
                  label={t('template:Show only autoevaluation exercises')}
                  onChange={(e, value) => onChangeIsAutoevaluative(value)}
                  checked={isAutoevaluative}
                />
              </div>
            </div>
          </div>
        </div>
      </LayoutCalendar>
    </Dialog>
  );
};

DialogSearchQuestionsNew.propTypes = {
  t: PropTypes.func,
  open: PropTypes.bool,
  onAdd: PropTypes.func,
  onCancel: PropTypes.func,
  alreadyAddedQuestions: PropTypes.array,
  questionType: PropTypes.string,
  courseGuid: PropTypes.string,
  selectJustOne: PropTypes.bool,
};

export default DialogSearchQuestionsNew;
